import { URLInputFieldProps } from "./types";
import { useController } from "react-hook-form";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { inputStyles } from "./inputStyles";
import { URLInput } from "@/components/URLInput";

export const URLInputField = ({
  field,
  form,
  stepId,
  size = "desktop",
}: URLInputFieldProps) => {
  const { trigger, watch } = form;
  const [isInitiated, setIsInitiated] = useState(false);
  const fieldsInputs = watch(stepId);
  const isDisplayed = field.showIf ? field.showIf?.(fieldsInputs) : true;
  const key = `${stepId}.${field.id}`;
  const {
    field: { onChange, value },
    fieldState: { error, isDirty },
  } = useController({
    rules: {
      required: isDisplayed ? field.required : false,
      validate: (val) => {
        if (!val) return true;

        const domainPart = val.startsWith("https://") ? val.substring(8) : val;

        const urlPattern =
          /^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9])\.)+([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9-]*[A-Za-z0-9])(\/.*)*/;

        if (!urlPattern.test(domainPart)) {
          return "Please enter a valid URL";
        }

        return field.validation?.(val, fieldsInputs) || true;
      },
    },
    control: form.control,
    name: key,
  });

  useEffect(() => {
    if (!isDisplayed && error) {
      form.clearErrors(key);
      setIsInitiated(false);
    }
  }, [form, key, isDisplayed, error]);

  useEffect(() => {
    if (!isDisplayed && value) {
      onChange("");
      setIsInitiated(false);
    }
  }, [isDisplayed, value, onChange]);

  useEffect(() => {
    if (isDisplayed) {
      setIsInitiated(true);
      trigger(key);
    }
  }, [isDisplayed, key, trigger, isInitiated]);

  if (!isDisplayed) {
    return null;
  }

  return (
    <div key={field.id}>
      <URLInput
        value={value}
        onChange={onChange}
        placeholder={
          typeof field.placeholder === "function"
            ? field.placeholder(fieldsInputs)
            : field.placeholder
        }
        label={
          typeof field.label === "function"
            ? field.label(fieldsInputs)
            : field.label
        }
        tooltip={field.tooltip}
        readOnly={field.readOnly}
        error={error?.message}
        showError={isDirty}
        className={cn(inputStyles.container({ size }))}
      />
    </div>
  );
};
